import { useMemo, useRef, useState } from 'react';
import { Copy, Download, Loader2 } from 'lucide-react';
import { useToast } from '../../context/ToastContext';
import { RECEIPT_IMAGE_ACTIONS_ENABLED } from '../../config/receiptUi';
import ReceiptLayoutV2 from './ReceiptLayoutV2';

const maskCardNumber = (value) => {
  const raw = String(value ?? '').trim();
  if (!raw || raw.includes('*')) return raw || '-';
  const digits = raw.replace(/\D/g, '');
  if (digits.length < 12) return raw;
  return `${digits.slice(0, 6)}${'*'.repeat(Math.max(4, digits.length - 10))}${digits.slice(-4)}`;
};

const waitForImages = async (target) => {
  const images = Array.from(target?.querySelectorAll('img') || []);
  await Promise.all(images.map(async (image) => {
    if (image.complete) return;
    try {
      await image.decode();
    } catch (_) {
      // 로고 로딩 실패는 html2canvas가 처리하도록 둔다.
    }
  }));
};

const canvasToPngBlob = (canvas) => new Promise((resolve, reject) => {
  canvas.toBlob((blob) => {
    if (blob) resolve(blob);
    else reject(new Error('전표 이미지를 생성하지 못했습니다.'));
  }, 'image/png');
});

const saveBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export default function ReceiptImageActions({
  data,
  viewMode = 'approval',
  forceEnabled = false,
  downloadOnly = false,
  downloadLabel = '저장',
}) {
  const captureRef = useRef(null);
  const [workingAction, setWorkingAction] = useState('');
  const { showToast } = useToast();

  const safeData = useMemo(() => ({
    ...data,
    cardNumberMasked: maskCardNumber(data?.cardNumberMasked),
  }), [data]);

  if ((!RECEIPT_IMAGE_ACTIONS_ENABLED && !forceEnabled) || !data) return null;

  const filename = `전표_${viewMode === 'cancel' ? '취소' : '승인'}_${String(data.approvalNumber || '내역').replace(/[^0-9A-Za-z가-힣_-]/g, '')}.png`;

  const createPngBlob = async () => {
    const target = captureRef.current;
    if (!target) throw new Error('전표 화면을 찾을 수 없습니다.');
    if (document.fonts?.ready) await document.fonts.ready;
    await waitForImages(target);
    const { default: html2canvas } = await import('html2canvas');
    const canvas = await html2canvas(target, {
      backgroundColor: '#ffffff',
      scale: 2,
      useCORS: true,
      logging: false,
      scrollX: 0,
      scrollY: 0,
      windowWidth: 420,
    });
    return canvasToPngBlob(canvas);
  };

  const handleDownload = async () => {
    if (workingAction) return;
    setWorkingAction('download');
    try {
      const blob = await createPngBlob();
      saveBlob(blob, filename);
      showToast('전표 이미지를 저장했습니다.');
    } catch (error) {
      showToast(error?.message || '전표 이미지 저장에 실패했습니다.', 'error');
    } finally {
      setWorkingAction('');
    }
  };

  const handleCopy = async () => {
    if (workingAction) return;
    setWorkingAction('copy');
    const blobPromise = createPngBlob();
    try {
      if (!navigator.clipboard?.write || !window.ClipboardItem) {
        const blob = await blobPromise;
        saveBlob(blob, filename);
        showToast('이미지 복사를 지원하지 않아 파일로 저장했습니다.');
        return;
      }
      await navigator.clipboard.write([
        new window.ClipboardItem({ 'image/png': blobPromise }),
      ]);
      showToast('전표 이미지를 복사했습니다. 카카오톡에 붙여넣어 주세요.');
    } catch (error) {
      try {
        const blob = await blobPromise;
        saveBlob(blob, filename);
        showToast('이미지 복사가 허용되지 않아 파일로 저장했습니다.');
      } catch (_) {
        showToast(error?.message || '전표 이미지 복사에 실패했습니다.', 'error');
      }
    } finally {
      setWorkingAction('');
    }
  };

  const buttonClass = 'inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-[#d7dce0] bg-white px-3 py-2 text-[12px] font-bold text-[#4e5968] hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)] disabled:cursor-not-allowed disabled:opacity-60';

  return (
    <>
      <div className="mb-3 flex gap-2" aria-label="전표 이미지 기능">
        <button type="button" onClick={handleDownload} disabled={Boolean(workingAction)} className={buttonClass} aria-label="전표 이미지 저장">
          {workingAction === 'download' ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
          {downloadLabel}
        </button>
        {!downloadOnly ? (
          <button type="button" onClick={handleCopy} disabled={Boolean(workingAction)} className={buttonClass} aria-label="전표 이미지 복사">
            {workingAction === 'copy' ? <Loader2 size={15} className="animate-spin" /> : <Copy size={15} />}
            복사
          </button>
        ) : null}
      </div>
      <div
        ref={captureRef}
        aria-hidden="true"
        style={{
          position: 'fixed',
          left: '-10000px',
          top: 0,
          width: '400px',
          background: '#fff',
          pointerEvents: 'none',
          contain: 'layout paint style',
        }}
      >
        <ReceiptLayoutV2 data={safeData} viewMode={viewMode} />
      </div>
    </>
  );
}
